import { z } from 'zod';
import { Users } from '../db-entries/user.js';
import { CustomProfileFieldType, customProfileFieldTypeGuard, fieldPartGuard, userProfileAddressKeys, userProfileGuard, } from '../foundations/index.js';
import { userOnboardingDataKey } from './onboarding.js';
import { defaultTenantIdKey } from './tenant.js';
import { consoleUserPreferenceKey, guideRequestsKey } from './user.js';
const baseProfileFieldGuard = z.object({
    name: z.string(),
    type: customProfileFieldTypeGuard,
    label: z.string(),
    description: z.string().optional(),
    required: z.boolean(),
});
export const textProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Text),
    config: z
        .object({
        placeholder: z.string().optional(),
        minLength: z.number().optional(),
        maxLength: z.number().optional(),
    })
        .optional(),
});
export const numberProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Number),
    config: z
        .object({
        placeholder: z.string().optional(),
        minValue: z.number().optional(),
        maxValue: z.number().optional(),
    })
        .optional(),
});
export const dateProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Date),
    config: z
        .object({
        placeholder: z.string().optional(),
        format: z.string(),
        customFormat: z.string().optional(),
    })
        .optional(),
});
export const checkboxProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Checkbox),
    config: z.object({
        options: z.array(z.object({ label: z.string(), value: z.string() })),
    }),
});
export const selectProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Select),
    config: z.object({
        placeholder: z.string().optional(),
        options: z.array(z.object({ label: z.string(), value: z.string() })),
    }),
});
export const urlProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Url),
    config: z
        .object({
        placeholder: z.string().optional(),
    })
        .optional(),
});
export const regexProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Regex),
    config: z.object({
        placeholder: z.string().optional(),
        format: z.string(),
    }),
});
export const addressProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Address),
    config: z.object({
        parts: z.array(fieldPartGuard.omit({ name: true }).extend({
            name: z.enum(userProfileAddressKeys),
        })),
    }),
});
export const fullnameKeys = userProfileGuard
    .pick({
    givenName: true,
    middleName: true,
    familyName: true,
})
    .keyof().options;
export const fullnameProfileFieldGuard = baseProfileFieldGuard.extend({
    type: z.literal(CustomProfileFieldType.Fullname),
    config: z.object({
        parts: z.array(fieldPartGuard.omit({ name: true }).extend({
            name: z.enum(fullnameKeys),
        })),
    }),
});
export const customProfileFieldUnionGuard = z.discriminatedUnion('type', [
    textProfileFieldGuard,
    numberProfileFieldGuard,
    dateProfileFieldGuard,
    checkboxProfileFieldGuard,
    selectProfileFieldGuard,
    urlProfileFieldGuard,
    regexProfileFieldGuard,
    addressProfileFieldGuard,
    fullnameProfileFieldGuard,
]);
export const nameAndAvatarGuard = z
    .object({
    name: z.string(),
    avatar: z.string().url().or(z.literal('')),
})
    .partial();
export const builtInProfileGuard = nameAndAvatarGuard.merge(z.object({ profile: userProfileGuard }));
export const builtInCustomProfileFieldKeys = Object.freeze(builtInProfileGuard.merge(userProfileGuard).keyof().options);
export const updateCustomProfileFieldDataGuard = z.discriminatedUnion('type', [
    textProfileFieldGuard.omit({ name: true }),
    numberProfileFieldGuard.omit({ name: true }),
    dateProfileFieldGuard.omit({ name: true }),
    checkboxProfileFieldGuard.omit({ name: true }),
    selectProfileFieldGuard.omit({ name: true }),
    urlProfileFieldGuard.omit({ name: true }),
    regexProfileFieldGuard.omit({ name: true }),
    addressProfileFieldGuard.omit({ name: true }),
    fullnameProfileFieldGuard.omit({ name: true }),
]);
export const updateCustomProfileFieldSieOrderGuard = z.object({
    name: z.string(),
    sieOrder: z.number(),
});
export const signInIdentifierKeyGuard = Users.createGuard
    .pick({
    username: true,
    primaryEmail: true,
    primaryPhone: true,
})
    .extend({
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
});
export const reservedCustomDataKeyGuard = z
    .object({
    [userOnboardingDataKey]: z.string(),
    [guideRequestsKey]: z.string(),
    [consoleUserPreferenceKey]: z.string(),
    [defaultTenantIdKey]: z.string(),
})
    .partial();
export const reservedCustomDataKeys = Object.freeze(reservedCustomDataKeyGuard.keyof().options);
/**
 * Disallow sign-in identifiers related field keys in custom profile fields, as this is conflicting
 * with the built-in sign-in/sign-up experience flows.
 */
export const reservedSignInIdentifierKeys = Object.freeze(signInIdentifierKeyGuard.keyof().options);
export var supportedDateFormat;
(function (supportedDateFormat) {
    supportedDateFormat["US"] = "MM/dd/yyyy";
    supportedDateFormat["UK"] = "dd/MM/yyyy";
    supportedDateFormat["ISO"] = "yyyy-MM-dd";
    supportedDateFormat["Custom"] = "custom";
})(supportedDateFormat || (supportedDateFormat = {}));
